import React, { Fragment, useCallback } from 'react';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * URL detection regex
 * Matches http://, https://, and URLs starting with www.
 * Excludes trailing brackets and common punctuation.
 */
const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"'\])\[(]+/gi;

interface LinkTextProps {
  children: string;
  className?: string;
}

/**
 * Component that detects URLs in text and renders them with
 * "open in new tab" and "copy to clipboard" buttons
 */
export function LinkText({ children, className }: LinkTextProps) {
  const [copiedUrl, setCopiedUrl] = React.useState<string | null>(null);

  const handleCopy = useCallback(async (url: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Normalize URL (add https:// if starts with www.)
      const fullUrl = url.startsWith('www.') ? `https://${url}` : url;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(url);
      toast.success('Скопировано');
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  }, []);

  const handleOpen = useCallback((url: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Normalize URL (add https:// if starts with www.)
    const fullUrl = url.startsWith('www.') ? `https://${url}` : url;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }, []);

  if (!children) return null;

  // Find all URLs in the text
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  // Reset regex state
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(children)) !== null) {
    const url = match[0];
    const startIndex = match.index;

    // Add text before the URL
    if (startIndex > lastIndex) {
      parts.push(children.slice(lastIndex, startIndex));
    }

    // Add the URL with buttons
    parts.push(
      <Fragment key={key++}>
        <a
          href={url.startsWith('www.') ? `https://${url}` : url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>
        <span className="inline-flex items-center gap-0.5 ml-1 align-middle">
          <button
            type="button"
            onClick={(e) => handleOpen(url, e)}
            className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Открыть в новом окне"
          >
            <ExternalLink className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={(e) => handleCopy(url, e)}
            className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Копировать"
          >
            {copiedUrl === url ? (
              <Check className="w-3 h-3 text-green-600" />
            ) : (
              <Copy className="w-3 h-3" />
            )}
          </button>
        </span>
      </Fragment>
    );

    lastIndex = startIndex + url.length;
  }

  // Add remaining text after the last URL
  if (lastIndex < children.length) {
    parts.push(children.slice(lastIndex));
  }

  // If no URLs found, return plain text
  if (parts.length === 0) {
    return <span className={className}>{children}</span>;
  }

  return <span className={className}>{parts}</span>;
}

/**
 * Utility function to check if text contains any URLs
 */
export function containsUrl(text: string): boolean {
  URL_REGEX.lastIndex = 0;
  return URL_REGEX.test(text);
}
